import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";

const log = createFileLogger('USER_INTERACTION');

export const userPickOptionCommand: SelfContainedCommand = {
  id: 'user_pick_option',
  description: `Present choices to the user and get their selection.

This command shows a list of options and waits for the user to select one.
Use this when the user needs to choose from predefined options.

**Use Cases:**
• Choose between multiple vehicles
• Select a payment method
• Pick an account to use
• Choose from available options

**Option Format:**
Each option should have:
• id - Unique identifier
• label - Display text
• description (optional) - Additional details`,
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'prompt',
        type: 'string',
        description: 'Question/prompt (e.g., "Which vehicle would you like to register?")',
        required: true
      },
      {
        name: 'options',
        type: 'array',
        description: 'Array of options: [{id, label, description?}]',
        required: true
      },
      {
        name: 'allowMultiple',
        type: 'boolean',
        description: 'Allow selecting multiple options',
        required: false,
        defaultValue: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          prompt: 'Which vehicle would you like to register?',
          options: [
            { id: 'vehicle_1', label: 'Ford F-250', description: 'CA license plate 7ABC123' },
            { id: 'vehicle_2', label: 'Honda Civic', description: 'CA license plate 5XYZ789' }
          ]
        },
        expectedOutput: createSuccessResult('Option selected', {
          selectedId: 'vehicle_1',
          selectedLabel: 'Ford F-250'
        }),
        description: 'Choose vehicle',
        context: 'When user has multiple vehicles'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 5000,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { prompt, options, allowMultiple = false } = params;
    
    try {
      log.info('[user_pick_option] Requesting selection:', { prompt, optionCount: options?.length });
      
      if (!prompt || !options || !Array.isArray(options)) {
        return createErrorResult(
          'Missing required parameters',
          'prompt and options (array) are required',
          'validation',
          { data: params }
        );
      }
      
      return createSuccessResult(
        'Option selected',
        {
          prompt,
          options,
          allowMultiple,
          selectedId: '',
          selectedLabel: '',
          inputType: 'select',
          timestamp: new Date().toISOString()
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            requiresUserInteraction: true
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[user_pick_option] Error:', error);
      return createErrorResult(
        'Option selection failed',
        `Failed to get user selection: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { prompt, options },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};
